import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Icon } from '@iconify/react';
import dashboardStore from '../../store/dashboardStore';
import {
  buildMontoSucursalData,
  buildEscaneosSucursalData,
  buildDonutMetricasGen,
  buildCuponesSucursalStacked,
  buildCuponesGeneralesPie,
  buildOrigenClientesPie,
  buildMeserosBar,
  buildMeserosRegistroBar,
  formatMonto,
  parseMonto,
} from '../../utils/dashboardUtils';

// ─── Paleta de colores corporativa ───────────────────────────────────────────
const COLORS_PRIMARY = [
  '#4361ee', '#3a0ca3', '#7209b7', '#f72585', '#4cc9f0',
  '#06d6a0', '#ffd166', '#ef476f', '#118ab2', '#073b4c',
];

const COLORS_DONUT = ['#06d6a0', '#f72585'];

// ─── Opciones base compartidas ────────────────────────────────────────────────
const baseChartOptions = {
  chart: {
    toolbar: { show: false },
    fontFamily: 'inherit',
    background: 'transparent',
  },
  grid: {
    borderColor: 'rgba(0,0,0,0.06)',
    strokeDashArray: 4,
  },
  tooltip: {
    style: { fontSize: '13px' },
  },
  legend: {
    fontSize: '13px',
    fontWeight: 500,
  },
};

// ─── Componente: Filtros (Fechas y Sucursales) ─────────────────────────────────
const DashboardFilters = () => {
  const { fechaInicio, fechaFin, idSucursal, sucursales, cambiarFiltros } = dashboardStore();

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    cambiarFiltros({ [name]: value });
  };

  const handleSucursalChange = (e) => {
    cambiarFiltros({ idSucursal: parseInt(e.target.value) });
  };

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <div className="d-flex align-items-center gap-1">
        <label className="text-sm fw-medium mb-0">Desde:</label>
        <input
          type="date"
          className="form-control form-control-sm"
          name="fechaInicio"
          value={fechaInicio}
          min="2026-01-01"
          onChange={handleDateChange}
        />
      </div>
      <div className="d-flex align-items-center gap-1">
        <label className="text-sm fw-medium mb-0">Hasta:</label>
        <input
          type="date"
          className="form-control form-control-sm"
          name="fechaFin"
          value={fechaFin}
          min="2026-01-01"
          onChange={handleDateChange}
        />
      </div>
      <div className="d-flex align-items-center gap-1">
        <label className="text-sm fw-medium mb-0">Sucursal:</label>
        <select
          className="form-select form-select-sm w-auto"
          value={idSucursal}
          onChange={handleSucursalChange}
        >
          <option value={0}>Todas</option>
          {sucursales.map((suc) => (
            <option key={suc.ID_SUC} value={suc.ID_SUC}>
              {suc.NOMBRE_SUCURSAL_SUC}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

// ─── Componente: Métricas generales (tarjetas KPI) ───────────────────────────
const MetricasGenerales = ({ metricasGen }) => {
  if (!metricasGen.length) return null;
  const gen = metricasGen[0];

  const kpis = [
    { label: 'Miembros/Socios', value: gen.CLIENTES || 0, icon: 'mdi:account-group', color: 'bg-cyan' },
    { label: 'Escaneos', value: gen.ESCANEOS || 0, icon: 'mdi:qrcode-scan', color: 'bg-purple' },
    { label: 'INGRESO ANTES DE DESCUENTO', value: gen.MONTO_TOTAL, icon: 'mdi:cash-multiple', color: '#4361ee' },
    { label: 'Descuento', value: gen.DESCUENTO, icon: 'mdi:tag-outline', color: 'bg-warning-main' },
    { label: 'INGRESO FINAL', value: gen.MONTO_FINAL, icon: 'mdi:wallet', color: 'bg-info' },
    { label: 'Cupones', value: gen.CUPONES_REDIMIDOS || 0, icon: 'mdi:ticket-percent', color: 'bg-red' },
  ];

  return (
    <div className="row row-cols-xxxl-6 row-cols-lg-3 row-cols-sm-2 row-cols-1 gy-4 mb-24">
      {kpis.map(({ label, value, color }) => (
        <div className="col" key={label}>
          <div className="card shadow-none border h-100">
            <div className="card-body p-16">
              <p className="fw-medium text-primary-light mb-1 text-sm">{label}</p>
              <h6 className="mb-0 text-lg">
                {value != null && value !== '' ? (typeof value === 'string' ? value : Number(value).toLocaleString('es-MX')) : '0'}
              </h6>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Gráfica 1: Monto Total por Sucursal ─────────────────────────────────────
const GraficaMontoSucursal = ({ sucursales, metricasSuc }) => {
  const { labels, data } = useMemo(
    () => buildMontoSucursalData(sucursales, metricasSuc),
    [sucursales, metricasSuc]
  );

  const options = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'bar', id: 'monto-sucursal' },
    xaxis: {
      categories: labels,
      labels: { style: { fontSize: '12px' } },
    },
    yaxis: {
      labels: {
        minWidth: 50,
        formatter: (v) => {
          if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
          if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
          return `$${Math.round(v)}`;
        },
        style: { fontSize: '12px' },
      },
    },
    colors: COLORS_PRIMARY,
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: '50%',
        distributed: true,
      },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    tooltip: {
      ...baseChartOptions.tooltip,
      y: { formatter: (v) => formatMonto(v) },
    },
  };

  return (
    <div className="col-xxl-6 col-xl-12">
      <div className="card h-100">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-16">
            <h6 className="text-lg mb-0">Ingresos por Sucursal</h6>
            <span className="badge text-sm fw-semibold badge-secondary-50 text-secondary-500 px-8 py-4">
              Monto Total
            </span>
          </div>
          <ReactApexChart options={options} series={[{ name: 'Monto Total', data }]} type="bar" height={280} />
        </div>
      </div>
    </div>
  );
};

// ─── Gráfica 2: Escaneos por Sucursal ───────────────────────────────────────
const GraficaEscaneosSucursal = ({ sucursales, metricasSuc }) => {
  const { labels, data } = useMemo(
    () => buildEscaneosSucursalData(sucursales, metricasSuc),
    [sucursales, metricasSuc]
  );

  const options = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'bar', id: 'escaneos-sucursal' },
    xaxis: {
      categories: labels,
      labels: { style: { fontSize: '12px' } },
    },
    yaxis: {
      labels: {
        formatter: (v) => Math.round(v),
        style: { fontSize: '12px' },
      },
    },
    colors: ['#4cc9f0', '#7209b7', '#f72585'],
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: '50%',
        distributed: true,
      },
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: '12px', fontWeight: 600 },
      formatter: (v) => (v === 0 ? '' : v),
    },
    legend: { show: false },
    tooltip: {
      ...baseChartOptions.tooltip,
      y: { formatter: (v) => `${v} escaneos` },
    },
  };

  return (
    <div className="col-xxl-6 col-xl-12">
      <div className="card h-100">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-16">
            <h6 className="text-lg mb-0">Actividad por Sucursal</h6>
            <span className="badge text-sm fw-semibold badge-info-50 text-info-500 px-8 py-4">
              Escaneos
            </span>
          </div>
          <ReactApexChart options={options} series={[{ name: 'Escaneos', data }]} type="bar" height={280} />
        </div>
      </div>
    </div>
  );
};

// ─── Gráfica Nueva: Escaneos por Mesero ──────────────────────────────────────
const GraficaMeseros = ({ meseros }) => {
  const { labels, data, sucursales } = useMemo(
    () => buildMeserosBar(meseros),
    [meseros]
  );

  const options = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'bar', id: 'escaneos-meseros' },
    colors: ['#ffd166'],
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: true,
        columnWidth: '50%',
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => val,
      offsetY: 1,
      offsetX: 20,
      style: {
        fontSize: '12px',
        colors: ['#304758'],
      },
    },
    xaxis: {
      categories: labels,
      labels: { style: { colors: '#64748b' } },
    },
    tooltip: {
      ...baseChartOptions.tooltip,
      y: { formatter: (v, { dataPointIndex }) => `${v} escaneos en ${sucursales[dataPointIndex]}` },
    },
  };

  return (
    <div className="col-xxl-12 col-xl-12">
      <div className="card h-100">
        <div className="card-body">
          <h6 className="text-lg mb-16">Meseros con más escaneos</h6>
          {data.length === 0 ? (
            <div className="d-flex align-items-center justify-content-center" style={{ height: 300 }}>
              <p className="text-muted">Sin datos disponibles</p>
            </div>
          ) : (
            <ReactApexChart options={options} series={[{ name: 'Escaneos', data }]} type="bar" height={300} />
          )}
        </div>
      </div>
    </div>
  );
};

const GraficaMeserosRegistro = ({ meserosRegistro }) => {
  const { labels, data } = useMemo(
    () => buildMeserosRegistroBar(meserosRegistro),
    [meserosRegistro]
  );

  const options = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'bar', id: 'registro-meseros' },
    colors: ['#06d6a0'],
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: true,
        columnWidth: '50%',
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => val,
      offsetY: 1,
      offsetX: 12,
      style: {
        fontSize: '12px',
        colors: ['#304758'],
      },
    },
    xaxis: {
      categories: labels,
      labels: { style: { colors: '#64748b' } },
    },
    tooltip: {
      ...baseChartOptions.tooltip,
      y: { formatter: (v) => `${v} registros` },
    },
  };

  return (
    <div className="col-xxl-12 col-xl-12">
      <div className="card h-100">
        <div className="card-body">
          <h6 className="text-lg mb-16">Meseros más mencionados en registros</h6>
          {data.length === 0 ? (
            <div className="d-flex align-items-center justify-content-center" style={{ height: 300 }}>
              <p className="text-muted">Sin datos disponibles</p>
            </div>
          ) : (
            <ReactApexChart options={options} series={[{ name: 'Registros', data }]} type="bar" height={300} />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Gráfica 3: Donut Métricas Generales ────────────────────────────────────
const GraficaDonutMetricasGen = ({ metricasGen }) => {
  const { labels, data } = useMemo(
    () => buildDonutMetricasGen(metricasGen),
    [metricasGen]
  );

  const total = data.reduce((a, b) => a + b, 0);

  const options = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'donut', id: 'donut-metricas' },
    labels,
    colors: COLORS_DONUT,
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '14px',
              fontFamily: 'inherit',
            },
            value: {
              show: true,
              fontSize: '20px',
              fontFamily: 'inherit',
              fontWeight: 600,
              formatter: (val) => formatMonto(val),
            },
            total: {
              show: true,
              label: 'INGRESO ANTES DE DESCUENTO',
              color: '#4361ee',
              fontSize: '14px',
              fontFamily: 'inherit',
              formatter: () => formatMonto(parseMonto(metricasGen[0]?.MONTO_TOTAL || 0)),
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      ...baseChartOptions.tooltip,
      y: { formatter: (v) => formatMonto(v) },
    },
    legend: {
      position: 'bottom',
      ...baseChartOptions.legend,
    },
  };

  return (
    <div className="col-xxl-4 col-xl-6">
      <div className="card h-100">
        <div className="card-body">
          <h6 className="text-lg mb-16">Distribución Financiera</h6>
          {total === 0 ? (
            <div className="d-flex align-items-center justify-content-center" style={{ height: 280 }}>
              <p className="text-muted">Sin datos disponibles</p>
            </div>
          ) : (
            <ReactApexChart options={options} series={data} type="donut" height={280} />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Gráfica 4: Barras Apiladas — Cupones por Sucursal ──────────────────────
const GraficaCuponesApilados = ({ sucursales, cuponesSuc }) => {
  const { labels, series } = useMemo(
    () => buildCuponesSucursalStacked(sucursales, cuponesSuc),
    [sucursales, cuponesSuc]
  );

  const options = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'bar', stacked: true, id: 'cupones-apilados' },
    xaxis: {
      categories: labels,
      labels: { style: { fontSize: '12px' } },
    },
    yaxis: {
      labels: {
        formatter: (v) => Math.round(v),
        style: { fontSize: '12px' },
      },
    },
    colors: COLORS_PRIMARY,
    plotOptions: {
      bar: {
        borderRadius: 0,
        columnWidth: '50%',
      },
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: '11px', fontWeight: 600 },
      formatter: (v) => (v === 0 ? '' : v),
    },
    legend: {
      position: 'bottom',
      ...baseChartOptions.legend,
    },
    tooltip: {
      ...baseChartOptions.tooltip,
      y: { formatter: (v) => `${v} cupones` },
    },
  };

  return (
    <div className="col-xxl-8 col-xl-12">
      <div className="card h-100">
        <div className="card-body">
          <h6 className="text-lg mb-16">Cupones por Sucursal y Tipo</h6>
          {series.length === 0 ? (
            <div className="d-flex align-items-center justify-content-center" style={{ height: 280 }}>
              <p className="text-muted">Sin datos disponibles</p>
            </div>
          ) : (
            <ReactApexChart options={options} series={series} type="bar" height={280} />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Gráfica 5: Pie — Cupones Generales ─────────────────────────────────────
const GraficaCuponesGenerales = ({ cuponesGen }) => {
  const { labels, data } = useMemo(
    () => buildCuponesGeneralesPie(cuponesGen),
    [cuponesGen]
  );

  const total = data.reduce((a, b) => a + b, 0);

  const formatLabel = (val, seriesIndex) => {
    const amount = Math.round((val / 100) * total);
    return `${val.toFixed(1)}% (${amount.toLocaleString('es-MX')})`;
  };

  const options = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'pie', id: 'cupones-generales' },
    labels,
    colors: COLORS_PRIMARY,
    stroke: {
      show: false,
    },
    plotOptions: {
      pie: {
        expandOnClick: false,
        borderWidth: 0,
        dataLabels: {
          offset: -25,
          minAngleToEdge: 0,
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: formatLabel,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        colors: ['#ffffff'],
        textShadow: true,
        shadow: {
          top: 2,
          left: 2,
          blur: 4,
          color: '#000000',
          opacity: 0.9
        }
      },
    },
    tooltip: {
      ...baseChartOptions.tooltip,
      y: { formatter: (v) => `${v} cupones` },
    },
    legend: {
      position: 'bottom',
      ...baseChartOptions.legend,
    },
  };

  return (
    <div className="col-xxl-6 col-xl-6">
      <div className="card h-100">
        <div className="card-body">
          <h6 className="text-lg mb-16">Distribución de Cupones</h6>
          {total === 0 ? (
            <div className="d-flex align-items-center justify-content-center" style={{ height: 280 }}>
              <p className="text-muted">Sin datos disponibles</p>
            </div>
          ) : (
            <ReactApexChart options={options} series={data} type="pie" height={280} />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Gráfica 6: Pie — Origen de Clientes ────────────────────────────────────
const GraficaOrigenClientes = ({ origenClientes }) => {
  const { labels, data } = useMemo(
    () => buildOrigenClientesPie(origenClientes),
    [origenClientes]
  );

  const total = data.reduce((a, b) => a + b, 0);

  const formatLabel = (val, seriesIndex) => {
    const amount = Math.round((val / 100) * total);
    return `${val.toFixed(1)}% (${amount.toLocaleString('es-MX')})`;
  };

  const options = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'pie', id: 'origen-clientes' },
    labels,
    colors: ['#4361ee', '#06d6a0', '#ea9999', '#ef476f', '#4cc9f0'],
    stroke: {
      show: false,
    },
    plotOptions: {
      pie: {
        expandOnClick: false,
        borderWidth: 0,
        dataLabels: {
          offset: -25,
          minAngleToEdge: 0,
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: formatLabel,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        colors: ['#ffffff'],
        textShadow: true,
        shadow: {
          top: 2,
          left: 2,
          blur: 4,
          color: '#000000',
          opacity: 0.9
        }
      },
    },
    tooltip: {
      ...baseChartOptions.tooltip,
      y: { formatter: (v) => `${v} miembros/socios` },
    },
    legend: {
      position: 'bottom',
      ...baseChartOptions.legend,
    },
  };

  return (
    <div className="col-xxl-6 col-xl-6">
      <div className="card h-100">
        <div className="card-body">
          <h6 className="text-lg mb-16">Origen de Miembros/Socios</h6>
          {total === 0 ? (
            <div className="d-flex align-items-center justify-content-center" style={{ height: 280 }}>
              <p className="text-muted">Sin datos disponibles</p>
            </div>
          ) : (
            <ReactApexChart options={options} series={data} type="pie" height={280} />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Componente principal exportado ──────────────────────────────────────────
const DashboardCharts = () => {
  const {
    isLoading,
    error,
    sucursales,
    metricasSuc,
    metricasGen,
    cuponesSuc,
    cuponesGen,
    meseros,
    origenClientes,
    meserosRegistro,
    fechaInicio,
    fechaFin,
    idSucursal
  } = dashboardStore();

  const handleDownloadExcel = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`/p_metricas_excel?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}&idSucursal=${idSucursal}`);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Metricas_Dashboard_${fechaInicio}_al_${fechaFin}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading the file:', error);
      alert('Error descargando el archivo de excel');
    }
  };

  const sucursalSeleccionada = sucursales.find(s => s.ID_SUC === idSucursal)?.NOMBRE_SUCURSAL_SUC || 'Todas las sucursales';
  const sucursalesGraficas = idSucursal > 0 ? sucursales.filter(s => s.ID_SUC === idSucursal) : sucursales;

  return (
    <>
      {/* Header con filtros de período y sucursal */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <div>
          <h6 className="text-lg mb-1">Dashboard — Métricas</h6>
          <p className="text-sm text-secondary-light mb-0">
            Período: {fechaInicio} al {fechaFin} — {sucursalSeleccionada}
          </p>
        </div>
        <div className="d-flex align-items-center gap-3">
          {isLoading && (
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          )}
          <DashboardFilters />
          <button
            onClick={handleDownloadExcel}
            className="btn btn-success d-flex align-items-center gap-2 btn-sm"
            disabled={isLoading}
          >
            <Icon icon="mdi:file-excel-outline" className="text-lg" />
            Descargar
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger mb-24" role="alert">
          <strong>Error al cargar datos:</strong> {error}
        </div>
      )}

      {/* KPIs generales */}
      <MetricasGenerales metricasGen={metricasGen} />

      {/* Gráficas — fila 1 */}
      <div className="row gy-4 mb-24">
        <GraficaMontoSucursal sucursales={sucursalesGraficas} metricasSuc={metricasSuc} />
        <GraficaEscaneosSucursal sucursales={sucursalesGraficas} metricasSuc={metricasSuc} />
      </div>

      {/* Gráficas — fila 2 */}
      <div className="row gy-4 mb-24">
        <GraficaDonutMetricasGen metricasGen={metricasGen} />
        <GraficaCuponesApilados sucursales={sucursalesGraficas} cuponesSuc={cuponesSuc} />
      </div>

      {/* Gráficas — fila 3 */}
      <div className="row gy-4 mb-24">
        <GraficaCuponesGenerales cuponesGen={cuponesGen} />
        <GraficaOrigenClientes origenClientes={origenClientes} />
      </div>

      {/* Gráficas — fila 4 */}
      <div className="row gy-4 mb-24">
        <GraficaMeseros meseros={meseros} />
        <GraficaMeserosRegistro meserosRegistro={meserosRegistro} />
      </div>
    </>
  );
};

export default DashboardCharts;
